/*
 * Presco Flagging Tape consolidation - pure Style -> Length -> Color tree
 * derivation from a product's variants JSON. No hardcoded combo data: the
 * tree is built entirely from product.options / product.variants at
 * runtime, so it stays correct if variants are added or removed (e.g. CMG
 * later). This file is deployed byte-identical as assets/presco-tree.js in
 * the live theme once the theme-write blocker (see theme-files.md) is
 * resolved; this copy is what the Phase 3b checker's Node diff script
 * (tree-diff.js, same directory) requires to verify the shipped logic
 * against sku-mapping.json.
 *
 * Parity round (2026-08-24) additions: bareSku/mediaForVariant/titleForVariant
 * are small pure helpers for features 1 (dynamic H1 title) and 4 (alt-prefix
 * gallery filter). Colocated here rather than in a new asset file so the
 * additive file count stays at 6 native theme files + presco-title-map.js
 * (data only) - these are still "pure derivation logic", same spirit as
 * buildTree/resolveVariant, just deriving presentation data instead of a
 * variant. Verified by docs/recon/presco-consolidation/verify_gallery_and_titles.js.
 *
 * Page-parity fix round (2026-08-24, second pass) adds sortByPreference and
 * resolveDefaultVariant for fix item 7 (300 Ft length default). sortByPreference
 * itself stays data-free (a generic value re-orderer) - the actual '300 Ft'/
 * '150 Ft' literals live in sections/presco-consolidated-main.liquid's
 * inline script, not here. Verified by
 * docs/recon/presco-consolidation/verify_length_default.js.
 *
 * Color-grid redesign (2026-08-25, docs/specs/presco-color-grid-brief.md):
 * the Length PILL row was removed from the UI entirely (Length remains a
 * real Shopify option/variant axis - nothing here changed). sortByPreference
 * is now reused for a different purpose: ordering the ALWAYS-VISIBLE grid's
 * rows (150 Ft before 300 Ft within a film group) instead of ordering pills
 * (300 Ft before 150 Ft) - same generic function, different caller-supplied
 * preferredOrder array, no logic change. resolveDefaultVariant is also
 * reused unchanged, now called on every Style-pill switch (not just page
 * load) to pick the new style's default color+length, per the spec's
 * "reuse existing selection logic, do not reinvent it" instruction. The new
 * colorGridRows() function below is the one true addition: given a style,
 * it returns every (length, entries) row across ALL lengths (not just the
 * currently selected one) so the caller can render every color for both
 * lengths at once. It intentionally does NOT know about "film" (Solid vs.
 * patterned color-value shapes) - that grouping lives in
 * presco-swatch-map.js's groupSolidRowsByFilm(), which already owns
 * splitSolid/splitPattern. Verified by
 * docs/recon/presco-consolidation/simulate_grid.js.
 *
 * Single-product merge (2026-08-24/25, docs/specs/presco-single-product-merge-brief.md):
 * the Length option's stored values are now COMBINED strings ("150 Ft / 12
 * Pack", "300 Ft / 144 Pack", ...) - Pack Size folded into the Length axis
 * server-side (productOptionUpdate rename-in-place) since a 4th real
 * Shopify option isn't possible. Pack becomes its own first-class UI STATE
 * here, same shape as Style: parseCombinedLength()/combineLengthPack() are
 * the only new parsing primitives (a fixed " / <N> Pack" suffix grammar,
 * matching the literal option-value strings written during the merge);
 * colorGridRowsForPack() is colorGridRows() filtered to one pack and
 * re-keyed by BASE length ("150 Ft", not "150 Ft / 12 Pack") so every
 * existing pack-agnostic consumer (buildGridRow, You-picked box, COLOR
 * header) keeps working against plain base-length strings without knowing
 * packs exist. buildTree/resolveVariant/colorGridRows themselves are
 * UNCHANGED - they already treated the Length option's values as opaque
 * strings, so a combined value flows through them exactly like a plain one
 * always did; only the SECTION script (which now tracks state.pack
 * alongside state.style/length/color and always calls resolveVariant with
 * combineLengthPack(state.length, state.pack)) and this file's three new
 * exports are new. Verified by docs/recon/presco-consolidation/simulate_grid_merged.js.
 * Project: Shopify General / Presco flagging tape listing consolidation.
 * Date: 2026-08-24. Safe to delete if the Presco consolidation is abandoned.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.PrescoTree = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {

  // product: { options: [{name, position}], variants: [{id, sku, available/availableForSale, price, selectedOptions:[{name,value}], ...}] }
  // Returns: { tree: { [style]: { [length]: [{ color, variantId, sku, available, price }] } }, optionNames: [style,length,color] }
  function buildTree(product) {
    var options = product.options || [];
    if (options.length !== 3) {
      throw new Error('buildTree expects exactly 3 options, got ' + options.length);
    }
    var styleName = options[0].name;
    var lengthName = options[1].name;
    var colorName = options[2].name;

    var tree = {};
    var variants = product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      var opts = {};
      (v.selectedOptions || v.options || []).forEach(function (o) {
        if (o.name && o.value !== undefined) opts[o.name] = o.value;
      });
      // support the productSet-style flat option1/option2/option3 shape too
      if (Object.keys(opts).length === 0 && (v.option1 !== undefined)) {
        opts[styleName] = v.option1;
        opts[lengthName] = v.option2;
        opts[colorName] = v.option3;
      }
      var style = opts[styleName];
      var length = opts[lengthName];
      var color = opts[colorName];
      if (style === undefined || length === undefined || color === undefined) continue;

      if (!tree[style]) tree[style] = {};
      if (!tree[style][length]) tree[style][length] = [];
      var available = v.available !== undefined ? v.available : (v.availableForSale !== undefined ? v.availableForSale : null);
      tree[style][length].push({
        color: color,
        variantId: v.id,
        sku: v.sku,
        available: available,
        price: v.price !== undefined ? v.price : null
      });
    }
    return { tree: tree, optionNames: [styleName, lengthName, colorName] };
  }

  // The pack-size suffix is the sole interface contract between a full SKU
  // and its "bare" (pre-consolidation) form - both -12 and -144 SKUs live
  // on the ONE merged product now (see repricing-sop.md / theme-files.md).
  var SKU_SUFFIX_RE = /-(?:12|144)$/;
  function bareSku(sku) {
    return (sku || '').replace(SKU_SUFFIX_RE, '');
  }

  // media: [{alt, url, zoomUrl}] pulled from product.media (server-rendered).
  // sku: the resolved variant's full SKU (e.g. "CMOG-12").
  // fallback: {url, zoomUrl} - typically the variant's own featured_image -
  //   used only when no media's alt-FIRST-TOKEN equals the bare SKU (spec
  //   item 4's documented fallback). Returns [] only if there is truly no
  //   match and no fallback.
  function mediaForVariant(media, sku, fallback) {
    var bare = bareSku(sku);
    var matches = (media || []).filter(function (m) {
      var alt = (m.alt || '').trim();
      if (!alt) return false;
      var firstToken = alt.split(/\s+/)[0];
      return firstToken === bare;
    });
    if (matches.length === 0 && fallback && fallback.url) {
      matches = [{ url: fallback.url, zoomUrl: fallback.zoomUrl || fallback.url, alt: sku }];
    }
    return matches;
  }

  // titleMap: { [bareSku]: old 12-pack listing title } (presco-title-map.js).
  // packSize: "12"/"144"/12/144 - the 144-pack variant's H1 is the same old
  // title with the trailing "12 Roll Pack" replaced by "144 Roll Pack"
  // (build_title_map.py asserts every entry ends exactly in "12 Roll Pack",
  // so this is a safe literal suffix replace, never a partial/loose match).
  // Returns null if the SKU has no title-map entry (should not happen for
  // any of the 87 consolidated SKUs; caller falls back to product.title).
  function titleForVariant(titleMap, sku, packSize) {
    var bare = bareSku(sku);
    var title = titleMap && titleMap[bare];
    if (!title) return null;
    var pack = parseInt(packSize, 10);
    if (pack === 144) {
      title = title.replace(/12 Roll Pack$/, '144 Roll Pack');
    }
    return title;
  }

  // Returns the single variant entry for a given (style,length,color) selection,
  // or null if that combination does not exist (a true dead end - the UI
  // must never let this be reached, but the function itself reports it
  // rather than throwing so the caller can decide).
  function resolveVariant(treeResult, style, length, color) {
    var branch = treeResult.tree[style] && treeResult.tree[style][length];
    if (!branch) return null;
    for (var i = 0; i < branch.length; i++) {
      if (branch[i].color === color) return branch[i];
    }
    return null;
  }

  // Sorts `values` (a subset of the theme's own raw option-value order)
  // into the fixed presentation order `preferredOrder` - e.g. showing
  // 300 Ft before 150 Ft in the pills, WITHOUT touching Shopify's stored
  // option order (fix item 7: "THEME-SIDE ONLY, no Shopify option/variant
  // reorder"). Any value not present in preferredOrder keeps its original
  // relative order, appended after every matched value - defensive for a
  // future 3rd Length value this map doesn't know about, so an unknown
  // value is never silently dropped.
  function sortByPreference(values, preferredOrder) {
    var list = values || [];
    var order = preferredOrder || [];
    var known = [];
    var unknown = [];
    list.forEach(function (v) {
      if (order.indexOf(v) === -1) unknown.push(v);
    });
    order.forEach(function (p) {
      if (list.indexOf(p) !== -1) known.push(p);
    });
    return known.concat(unknown);
  }

  // Fix item 7's "300 Ft default" rule, as a pure function over an
  // already-built tree - no DOM/URL access (the caller checks
  // ?variant=/?sku= URL params FIRST; this is only the no-param
  // fallback). Precedence:
  //   1. Same Style+Color as `fallbackStyle`/`fallbackColor` (Shopify's
  //      own default variant), Length swapped to `preferredLength`.
  //   2. First variant whose Length === preferredLength, walking
  //      `styleOrder` for the style and `colorOrder` for the color
  //      (both theme-native option-value orders, not custom sorting).
  //   3. Returns null if no variant anywhere has `preferredLength` (the
  //      caller keeps its own already-known default in that case).
  // Single-product merge: `preferredLength` is now whatever the caller
  // passes (a COMBINED value, e.g. combineLengthPack('300 Ft', '12')) -
  // this function is unchanged and treats it as an opaque tree key, same
  // as it always has.
  function resolveDefaultVariant(treeResult, styleOrder, colorOrder, preferredLength, fallbackStyle, fallbackColor) {
    var swapped = resolveVariant(treeResult, fallbackStyle, preferredLength, fallbackColor);
    if (swapped) {
      return { style: fallbackStyle, length: preferredLength, color: fallbackColor, entry: swapped };
    }
    var styles = styleOrder || Object.keys(treeResult.tree);
    for (var i = 0; i < styles.length; i++) {
      var branch = treeResult.tree[styles[i]] && treeResult.tree[styles[i]][preferredLength];
      if (branch && branch.length) {
        var chosen = null;
        if (colorOrder) {
          for (var j = 0; j < colorOrder.length && !chosen; j++) {
            for (var k = 0; k < branch.length; k++) {
              if (branch[k].color === colorOrder[j]) { chosen = branch[k]; break; }
            }
          }
        }
        if (!chosen) chosen = branch[0];
        return { style: styles[i], length: preferredLength, color: chosen.color, entry: chosen };
      }
    }
    return null;
  }

  // Color-grid redesign (2026-08-25): returns every row for a style across
  // ALL its lengths - [{length, entries}], entries = the same tree-entry
  // shape resolveVariant/flattenTriples use ({color, variantId, sku,
  // available, price}). Ordered by lengthOrder (the theme's own stored
  // option-value order) re-sorted via sortByPreference/preferredLengthOrder
  // (e.g. ['150 Ft','300 Ft'] for the grid's "150 first" row order) - a
  // length absent from this style is simply skipped, never a blank row.
  // Pure/data-free: knows nothing about "film" - the caller (presco-swatch-
  // map.js's groupSolidRowsByFilm) buckets Solid rows further by film.
  // UNCHANGED by the single-product merge - see colorGridRowsForPack below
  // for the pack-aware wrapper the section script actually calls now.
  function colorGridRows(treeResult, style, lengthOrder, preferredLengthOrder) {
    var branch = treeResult.tree[style] || {};
    var lengths = (lengthOrder || []).filter(function (l) { return !!branch[l]; });
    var ordered = sortByPreference(lengths, preferredLengthOrder || []);
    return ordered.map(function (length) {
      return { length: length, entries: branch[length] };
    });
  }

  // Single-product merge: the Length option's stored values are combined
  // strings written by the merge's productOptionUpdate rename, always in
  // the literal shape "<base length> / <N> Pack" (e.g. "150 Ft / 12 Pack").
  // This is a fixed grammar (not derived/guessed) matching exactly what
  // was written to Shopify - see docs/specs/presco-single-product-merge-brief.md
  // Part 1 item 2. Returns null for any value not in that shape (defensive;
  // should not happen against the real 4-value catalog).
  var COMBINED_LENGTH_RE = /^(.+) \/ (\d+) Pack$/;
  // LEGACY_PACK is a sentinel (never a real pack number) for a product
  // whose Length option values were never renamed with a pack suffix -
  // e.g. the parked 144-pack draft (read-only, out of scope for the merge,
  // pending Andrew-gated deletion), which still carries plain "150 Ft"/
  // "300 Ft" values while sharing this same templateSuffix. Checker-found
  // gap (2026-08-25): the shared template must degrade gracefully on ANY
  // product using it whose Length values lack the pack suffix, not just
  // the merged product - parseCombinedLength used to return null for a
  // plain value, which made colorGridRowsForPack's `if (!parsed) return;`
  // guard skip every row, rendering a fully empty grid.
  var LEGACY_PACK = '__legacy__';
  function parseCombinedLength(value) {
    var m = COMBINED_LENGTH_RE.exec(value || '');
    if (m) {
      return { baseLength: m[1], pack: m[2], packLabel: m[2] + ' Pack' };
    }
    if (!value) return null;
    // Plain value, no " / N Pack" suffix - treat the whole value as the
    // base length under the LEGACY_PACK sentinel so every existing
    // pack-aware consumer (colorGridRowsForPack, availablePacks, the
    // pack-switch handlers) still finds a row/branch instead of an empty
    // one. combineLengthPack(baseLength, LEGACY_PACK) below reverses this
    // back to the exact original raw value (round-trip safe), which is
    // required for resolveVariant to find the right tree branch.
    return { baseLength: value, pack: LEGACY_PACK, packLabel: '', legacy: true };
  }
  function combineLengthPack(baseLength, pack) {
    if (pack === LEGACY_PACK) return baseLength;
    return baseLength + ' / ' + pack + ' Pack';
  }

  // Pack-aware wrapper around colorGridRows(): scopes a style's rows to
  // ONE pack and re-keys each row's `length` from the combined tree value
  // down to just the base length ("150 Ft"), so every existing
  // pack-agnostic caller (buildGridRow, You-picked box, COLOR header) can
  // keep working in plain base-length terms - Pack Size is selected via
  // its OWN pill row, orthogonal to what a color-grid row displays.
  // `preferredBaseLengthOrder` is the same kind of array colorGridRows
  // already takes (e.g. ['150 Ft','300 Ft']), just compared against BASE
  // lengths instead of combined ones.
  function colorGridRowsForPack(treeResult, style, pack, lengthOrder, preferredBaseLengthOrder) {
    var branch = treeResult.tree[style] || {};
    var baseLengths = [];
    var rawByBase = {};
    (lengthOrder || []).forEach(function (raw) {
      if (!branch[raw]) return;
      var parsed = parseCombinedLength(raw);
      if (!parsed || String(parsed.pack) !== String(pack)) return;
      if (baseLengths.indexOf(parsed.baseLength) === -1) baseLengths.push(parsed.baseLength);
      rawByBase[parsed.baseLength] = raw;
    });
    var ordered = sortByPreference(baseLengths, preferredBaseLengthOrder || []);
    return ordered.map(function (baseLength) {
      return { length: baseLength, entries: branch[rawByBase[baseLength]] };
    });
  }

  // Flattens the tree back to a list of {style,length,color,sku} triples -
  // used by the checker to diff against sku-mapping.json's 87 rows.
  function flattenTriples(treeResult) {
    var out = [];
    var tree = treeResult.tree;
    Object.keys(tree).forEach(function (style) {
      Object.keys(tree[style]).forEach(function (length) {
        tree[style][length].forEach(function (entry) {
          out.push({ style: style, length: length, color: entry.color, sku: entry.sku });
        });
      });
    });
    return out;
  }

  return {
    LEGACY_PACK: LEGACY_PACK,
    buildTree: buildTree,
    resolveVariant: resolveVariant,
    flattenTriples: flattenTriples,
    bareSku: bareSku,
    mediaForVariant: mediaForVariant,
    titleForVariant: titleForVariant,
    sortByPreference: sortByPreference,
    resolveDefaultVariant: resolveDefaultVariant,
    colorGridRows: colorGridRows,
    parseCombinedLength: parseCombinedLength,
    combineLengthPack: combineLengthPack,
    colorGridRowsForPack: colorGridRowsForPack
  };
});
